"use client";

import React from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { CircleAlert, ArrowRight, Coins, TrendingDown, Wrench } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  year: "Year 1",
  value: 30000,
  maintenance: 500
}, {
  year: "Year 2",
  value: 24000,
  maintenance: 1200
}, {
  year: "Year 3",
  value: 19000,
  maintenance: 2100
}, {
  year: "Year 4",
  value: 15000,
  maintenance: 3500
}, {
  year: "Year 5",
  value: 12000,
  maintenance: 5200
}, {
  year: "Year 6",
  value: 9500,
  maintenance: 7500
}, {
  year: "Year 7",
  value: 7500,
  maintenance: 10200
}, {
  year: "Year 8",
  value: 5500,
  maintenance: 13500
}, {
  year: "Year 9",
  value: 4000,
  maintenance: 17000
}, {
  year: "Year 10",
  value: 2500,
  maintenance: 21000
}];
const chartConfig = {
  value: {
    label: "Car Value ($)",
    color: "var(--primary)",
    icon: TrendingDown
  },
  maintenance: {
    label: "Cumulative Maintenance ($)",
    color: "var(--destructive)",
    icon: Wrench
  }
};
const features = [{
  title: "The Depreciation Curve",
  description: "Vehicles lose a significant portion of their value in the first few years. While holding onto an older car avoids this steep drop, its residual value continues to slowly decline.",
  icon: TrendingDown
}, {
  title: "The Maintenance Tipping Point",
  description: "As cars age, repair costs accelerate. When your annual repair bills start exceeding what you would pay for a year of new car payments, keeping the old vehicle becomes a financial liability.",
  icon: CircleAlert
}, {
  title: "Unlocking Hidden Value",
  description: "Even if your car requires more repairs than it's worth on the open market, it still holds value. Usable parts, recyclable metals, and salvage value mean your old car is always an asset.",
  icon: Coins
}];
export default function CarDepreciationValueMessaging() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: chartRef,
    isVisible: isChartVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: isCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="car-depreciation-value-messaging" className="bg-background text-foreground py-12 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4">
            Financial Insights
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            The true Cost of Keeping an Older Vehicle
          </h2>
          <p className="font-default text-lg text-muted-foreground leading-relaxed">
            Deciding whether to repair or replace an aging car is one of the most common financial dilemmas. Understanding the relationship between depreciation and maintenance is key to making the right choice.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div ref={chartRef} className={`transition-all duration-700 delay-100 ease-out ${isChartVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <Card className="shadow-lg border-border/50">
              <CardHeader>
                <CardTitle className="font-default text-xl">Value vs. Maintenance Over Time</CardTitle>
                <CardDescription className="font-default">
                  Notice the intersection where cumulative repair costs surpass the vehicle's actual worth.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="min-h-[200px] md:min-h-[300px] w-full">
                  <LineChart accessibilityLayer data={chartData} margin={{
                  left: 12,
                  right: 12,
                  top: 12,
                  bottom: 12
                }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
                    <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={8} className="text-xs font-default fill-muted-foreground" />
                    <YAxis tickLine={false} axisLine={false} tickMargin={8} tickFormatter={value => `$${value / 1000}k`} className="text-xs font-default fill-muted-foreground" />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                    <Line dataKey="value" type="monotone" stroke="var(--color-value)" strokeWidth={3} dot={false} activeDot={{
                    r: 6
                  }} />
                    <Line dataKey="maintenance" type="monotone" stroke="var(--color-maintenance)" strokeWidth={3} dot={false} activeDot={{
                    r: 6
                  }} />
                    <ChartLegend content={<ChartLegendContent />} />
                  </LineChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          <div className={`space-y-6 transition-all duration-700 delay-200 ease-out ${isChartVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <h3 className="font-default text-2xl md:text-3xl font-bold">
              Finding the Tipping Point
            </h3>
            <p className="font-default text-muted-foreground text-lg">
              Every vehicle eventually reaches a point where it costs more to keep on the road than it's actually worth. This is often referred to as being "totaled by maintenance."
            </p>
            <ul className="space-y-4">
              {["Are your annual repair bills higher than 12 months of new car payments?", "Is the cost of the next major repair more than 50% of the car's current value?", "Is the vehicle spending more time in the shop than on the road?"].map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                  <div className="mt-1 bg-primary/10 p-1 rounded-full shrink-0">
                    <CircleAlert className="w-4 h-4 text-primary" />
                  </div>
                  <span className="font-default text-foreground">{item}</span>
                </li>)}
            </ul>
            <div className="pt-4">
              <Link to="/SellMyCarPage">
                <Button size="lg" className="font-default w-full sm:w-auto group">
                  Evaluate Your Car's Value
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div ref={cardsRef} className={`grid md:grid-cols-3 gap-6 transition-all duration-700 delay-300 ease-out ${isCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {features.map((feature, i) => <Card key={i} data-index={i} className="bg-card border-border/50 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="font-default text-xl">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-default text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}